/**
 * TCVelik Export/Import Service
 * Handles exporting and importing content in TCVelik format (.tvoe files)
 */
class TCVelikExportImportService {
  constructor() {
    this.genericType = null;
    this.genericProgressActive = false;
    this.genericCurrentStep = 0;
    this.genericTotalSteps = 0;
  }

  /**
   * Convert image URLs to base64 format
   * @param {string} url - Image URL to convert
   * @returns {Promise<string>} Base64 data URL
   */
  async convertToBase64(url) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', url, error);
      return url; // Return original URL if conversion fails
    }
  }

  /**
   * Process HTML content and convert images to base64
   * @param {string} htmlContent - HTML content to process
   * @param {Function} progressCallback - Callback for progress updates
   * @returns {Promise<string>} Processed HTML with base64 images
   */
  async processImagesToBase64(htmlContent, progressCallback = null) {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlContent, 'text/html');
    const imgs = htmlDoc.getElementsByTagName('img');

    if (progressCallback) {
      progressCallback({
        type: 'tcvoe',
        active: true,
        currentStep: 0,
        totalSteps: imgs.length
      });
    }

    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      try {
        if (progressCallback) {
          progressCallback({
            type: 'tcvoe',
            active: true,
            currentStep: i + 1,
            totalSteps: imgs.length
          });
        }
        const base64 = await this.convertToBase64(img.src);
        img.src = base64;
      } catch (error) {
        console.error('Error converting image:', img.src, error);
      }
    }

    if (progressCallback) {
      progressCallback({
        type: 'tcvoe',
        active: false,
        currentStep: imgs.length,
        totalSteps: imgs.length
      });
    }

    return htmlDoc.body.innerHTML;
  }

  /**
   * Export content to TCVelik format as HTML
   * @param {Object} options - Export options
   * @param {string} options.title - Title of the content
   * @param {string} options.description - Description of the content
   * @param {string} options.htmlContent - HTML content to export
   * @param {Function} options.progressCallback - Progress callback function
   * @returns {Promise<void>}
   */
  async exportToTCVelik({ title, description, htmlContent, progressCallback = null }) {
    try {
      // Process images to base64
      const processedHTML = await this.processImagesToBase64(htmlContent, progressCallback);

      // Create the TCVelik export object
      const exportData = {
        Title: title,
        Description: description,
        Content: processedHTML
      };

      // Generate HTML with branding and proper step rendering
      const htmlExport = this.generateHTMLExport(exportData);

      // Create and download the file
      const blob = new Blob([htmlExport], { type: 'text/html' });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = title.replace(/\s+/g, '-') + '.html';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      return { success: true };
    } catch (error) {
      console.error('Error exporting to TCVelik format:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Import content from TCVelik format
   * @param {File} file - The .tvoe or .html file to import
   * @param {Function} successCallback - Callback for successful import
   * @param {Function} errorCallback - Callback for import errors
   * @returns {Promise<void>}
   */
  async importFromTCVelik(file, successCallback = null, errorCallback = null) {
    try {
      // Validate file type
      if (!file || (!file.name.endsWith('.tvoe') && !file.name.endsWith('.html'))) {
        const error = 'Please select a valid .tvoe or .html file';
        if (errorCallback) errorCallback(error);
        return { success: false, error };
      }

      // Read file content
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      let importData;

      if (file.name.endsWith('.html')) {
        // Handle HTML files with embedded JSON data
        importData = this.extractDataFromHTML(content);
        if (!importData) {
          const error = 'Invalid TCVelik HTML file: no embedded data found';
          if (errorCallback) errorCallback(error);
          return { success: false, error };
        }
      } else {
        // Handle JSON files
        importData = JSON.parse(content);
      }

      // Validate required fields
      if (!importData.Title || !importData.Content) {
        const error = 'Invalid TCVelik file format: missing required fields';
        if (errorCallback) errorCallback(error);
        return { success: false, error };
      }

      // Call success callback with parsed data
      if (successCallback) {
        successCallback({
          title: importData.Title,
          description: importData.Description || '',
          content: importData.Content
        });
      }

      return { 
        success: true, 
        data: {
          title: importData.Title,
          description: importData.Description || '',
          content: importData.Content
        }
      };
    } catch (error) {
      console.error('Error importing TCVelik file:', error);
      const errorMessage = error.message || 'Failed to import TCVelik file';
      if (errorCallback) errorCallback(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Trigger file input for import
   * @param {HTMLElement} fileInputRef - Reference to the file input element
   */
  triggerFileImport(fileInputRef) {
    if (fileInputRef && fileInputRef.click) {
      fileInputRef.click();
    }
  }

  /**
   * Handle file input change event
   * @param {Event} event - File input change event
   * @param {Function} successCallback - Callback for successful import
   * @param {Function} errorCallback - Callback for import errors
   * @param {HTMLElement} fileInputRef - Reference to the file input element (to clear value)
   */
  async handleFileInputChange(event, successCallback = null, errorCallback = null, fileInputRef = null) {
    const file = event.target.files[0];
    if (!file) return;

    const result = await this.importFromTCVelik(file, successCallback, errorCallback);
    
    // Clear the file input
    if (fileInputRef) {
      fileInputRef.value = '';
    }

    return result;
  }

  /**
   * Generate HTML export with branding and proper step rendering
   * @param {Object} exportData - The export data object
   * @returns {string} Complete HTML document
   */
  generateHTMLExport(exportData) {
    // Always include the raw content, whether it has structured steps or not
    const contentHTML = this.renderContentLikeOnboardingPDF(exportData.Content);
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${this.escapeHtml(exportData.Title)} - TCVelik Export</title>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet">
    <style>
        ${this.getExportStyles()}
    </style>
</head>
<body>
    <div class="export-container">
        <div class="export-header">
            <h1 class="export-title">${this.escapeHtml(exportData.Title)}</h1>
            ${exportData.Description ? `<div class="export-description">${this.escapeHtml(exportData.Description)}</div>` : ''}
        </div>
        
        <div class="export-content">
            ${contentHTML}
        </div>
        
        <div class="export-footer">
            <div class="tcvelik-branding">
                <div class="branding-content">
                    <div class="branding-text">
                        <i class="fas fa-video"></i>
                        <span>Recorded with <strong>TCVelik</strong></span>
                    </div>
                    <a href="https://tcvelik.com" target="_blank" class="branding-link">
                        <i class="fas fa-external-link-alt"></i>
                        Visit TCVelik
                    </a>
                </div>
            </div>
            
            <div class="download-prompt">
                <div class="prompt-content">
                    <h3><i class="fas fa-download"></i> For the best experience</h3>
                    <p>Download the Personal edition (free forever) to create, edit, and share your own onboarding content.</p>
                    <a href="https://tcvelik.com/downloads" target="_blank" class="download-button">
                        <i class="fas fa-download"></i>
                        Download Free Personal Edition
                    </a>
                </div>
            </div>
        </div>
        
        <!-- Fixed delayed message overlay -->
        <div id="delayed-message" class="delayed-message-overlay" style="display: none;">
            <div class="delayed-message">
                <div class="message-content">
                    <div class="message-header">
                        <i class="fas fa-info-circle"></i>
                        <div class="message-text">
                            <p class="message-title">Do not lose track of this document.</p>
                            <p>This document is in TCVelik compatible format. Download the Personal Edition and import it to have interactive steps, keep track of your progress, organize it, change it and share it with your friends and team.</p>
                        </div>
                        <button class="close-message" onclick="this.parentElement.parentElement.parentElement.style.display='none'">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="message-buttons">
                        <a href="https://tcvelik.com/downloads" target="_blank" class="btn-download">Download</a>
                        <a href="https://tcvelik.com" target="_blank" class="btn-website">Visit our website</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
    <script>
        // Embedded JSON data for TCVelik import
        window.TCVelikExportData = ${JSON.stringify(exportData)};
        
        // Add click handlers for step expansion (like OnboardingPDF)
        document.addEventListener('DOMContentLoaded', function() {
            document.querySelectorAll('[data-type="draggable-item"]').forEach(step => {
                const titleElement = step.querySelector('p:first-child');
                if (titleElement) {
                    titleElement.addEventListener('click', function() {
                        step.classList.toggle('expanded');
                    });
                }
            });
            
            // Show delayed message after 3-4 seconds
            setTimeout(function() {
                const delayedMessage = document.getElementById('delayed-message');
                if (delayedMessage) {
                    delayedMessage.style.display = 'flex';
                    delayedMessage.style.opacity = '0';
                    delayedMessage.style.transform = 'translateY(-20px)';
                    
                    // Animate in
                    setTimeout(function() {
                        delayedMessage.style.transition = 'all 0.5s ease';
                        delayedMessage.style.opacity = '1';
                        delayedMessage.style.transform = 'translateY(0)';
                    }, 50);
                    
                    // Auto-hide after 20 seconds
                    setTimeout(function() {
                        if (delayedMessage.style.display !== 'none') {
                            delayedMessage.style.transition = 'all 0.5s ease';
                            delayedMessage.style.opacity = '0';
                            delayedMessage.style.transform = 'translateY(-20px)';
                            
                            setTimeout(function() {
                                delayedMessage.style.display = 'none';
                            }, 500);
                        }
                    }, 20000); // 20 seconds
                }
            }, 3500); // 3.5 seconds
        });
    </script>
</body>
</html>`;
  }

  /**
   * Render content like OnboardingPDF (preserving original structure)
   * @param {string} htmlContent - HTML content to render
   * @returns {string} Rendered HTML content
   */
  renderContentLikeOnboardingPDF(htmlContent) {
    if (!htmlContent) return '';
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Process each draggable item (step) like OnboardingPDF does
    const stepElements = doc.querySelectorAll('[data-type="draggable-item"]');
    
    stepElements.forEach(stepElement => {
      // Convert first paragraph to h2 (like OnboardingPDF does)
      const firstParagraph = stepElement.querySelector('p:first-child');
      if (firstParagraph) {
        const h2 = doc.createElement('h2');
        h2.innerHTML = firstParagraph.innerHTML;
        stepElement.replaceChild(h2, firstParagraph);
      }
      
      // Remove the last paragraph (JSON metadata) - like OnboardingPDF
      const lastParagraph = stepElement.querySelector('p:last-child');
      if (lastParagraph) {
        lastParagraph.remove();
      }
      
      // Add expanded class to show content by default (like OnboardingPDF)
      stepElement.classList.add('expanded');
    });
    
    return doc.body.innerHTML;
  }

  /**
   * Parse steps from HTML content
   * @param {string} htmlContent - HTML content containing steps
   * @returns {Array} Array of step objects
   */
  parseStepsFromHTML(htmlContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const steps = [];
    
    const stepElements = doc.querySelectorAll('[data-type="draggable-item"]');
    
    stepElements.forEach((stepElement, index) => {
      const stepData = this.extractStepData(stepElement, index);
      if (stepData) {
        steps.push(stepData);
      }
    });
    
    return steps;
  }


  /**
   * Get CSS styles for the export
   * @returns {string} CSS styles
   */
  getExportStyles() {
    return `
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: 'Outfit', sans-serif;
        line-height: 1.6;
        color: #0f172a;
        background: #f8fafc;
      }

      .export-container {
        max-width: 1000px;
        margin: 0 auto;
        background: white;
        min-height: 100vh;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        position: relative;
      }

      .export-header {
        background: linear-gradient(135deg, #000000 0%, #a858f7 100%);
        color: white;
        padding: 3rem 2rem;
        text-align: center;
      }

      .export-title {
        font-size: 3rem;
        font-weight: 900;
        margin-bottom: 1rem;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .export-description {
        font-size: 1.2rem;
        opacity: 0.9;
        max-width: 600px;
        margin: 0 auto;
      }

      .export-content {
        padding: 2rem;
        font-size: 18px;
      }

      /* OnboardingPDF-like step styling */
      [data-type='draggable-item'] {
        background: white;
        border-radius: 12px;
        transition: all 0.3s ease;
        margin-bottom: 1em;
        margin-top: 1em;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      [data-subtype='informational'] {
        background: rgba(59, 130, 246, 0.1);
        color: #1e40af;
      }

      [data-subtype='warning'] {
        background: rgba(245, 158, 11, 0.1);
        color: #d97706;
      }

      [data-subtype='success'] {
        background: rgba(16, 185, 129, 0.1);
        color: #059669;
      }

      [data-subtype='error'] {
        background: rgba(239, 68, 68, 0.1);
        color: #dc2626;
      }

      [data-subtype='code'] {
        background: rgba(139, 92, 246, 0.1);
        color: #7c3aed;
      }

      [data-subtype='assign-sop'] {
        background: rgba(156, 39, 176, 0.1);
        color: #7b1fa2;
      }

      [data-subtype='form-capture'] {
        background: rgba(255, 152, 0, 0.1);
        color: #e65100;
      }

      [data-type='draggable-item'] h2 {
        display: flex;
        align-items: center;
        padding: 16px 135px 16px 16px;
        cursor: pointer;
        gap: 16px;
        flex-grow: 1;
        font-weight: 700;
        font-size: 1.2em;
        margin: 0;
        font-weight: 900;
        position: relative;
        border-bottom: 1px solid #a7a7a7;
      }

      [data-type='draggable-item'] p:nth-child(2) {
        display: block;
        font-size: 0.8em;
        padding: 16px 1em;
        background: white;
        color: black;
        margin-bottom: 10px;
      }

      [data-type='draggable-item'] img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      /* Typography like OnboardingPDF */
      .export-content h1 {
        font-size: 2.2em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content h2 {
        font-size: 2em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content h3 {
        font-size: 1.8em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content h4 {
        font-size: 1.6em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content h5 {
        font-size: 1.4em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content h6 {
        font-size: 1.2em;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
      }

      .export-content p {
        font-size: 1em;
        padding-bottom: 0.1em;
        padding-top: 0.1em;
        min-height: 1em;
        margin-bottom: 1rem;
      }

      /* Tables like OnboardingPDF */
      .export-content table {
        border-collapse: collapse;
        table-layout: fixed;
        width: 100%;
        margin: 0;
        overflow: hidden;
      }

      .export-content table th {
        font-weight: bold;
        font-size: 18px;
        text-align: left;
        background-color: #f1f3f5;
        padding: 6px 10px;
        padding-top: 5px;
        padding-bottom: 3px;
        border: 0;
      }

      .export-content table td {
        min-width: 1em;
        border: 1px solid #ced4da;
        padding: 3px 5px;
        vertical-align: top;
        box-sizing: border-box;
        position: relative;
      }

      .export-footer {
        background: #f8fafc;
        padding: 3rem 2rem;
        border-top: 1px solid #e2e8f0;
      }

      .tcvelik-branding {
        text-align: center;
        margin-bottom: 2rem;
        padding: 2rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .branding-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2rem;
        flex-wrap: wrap;
      }

      .branding-text {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.1rem;
        color: #374151;
      }

      .branding-text i {
        color: #667eea;
        font-size: 1.2rem;
      }

      .branding-link {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: #667eea;
        text-decoration: none;
        font-weight: 600;
        padding: 0.5rem 1rem;
        border: 2px solid #667eea;
        border-radius: 8px;
        transition: all 0.2s ease;
      }

      .branding-link:hover {
        background: #667eea;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      }

      .download-prompt {
        text-align: center;
        padding: 2rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-radius: 12px;
        box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
      }

      .prompt-content h3 {
        font-size: 1.5rem;
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
      }

      .prompt-content p {
        font-size: 1.1rem;
        margin-bottom: 1.5rem;
        opacity: 0.9;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
      }

      .download-button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background: white;
        color: #667eea;
        text-decoration: none;
        font-weight: 700;
        padding: 1rem 2rem;
        border-radius: 8px;
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .download-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      }

      .delayed-message-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        display: flex;
        justify-content: center;
        padding: 1rem 2rem;
        pointer-events: none;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
      }

      .delayed-message {
        max-width: 1000px;
        width: 100%;
        padding: 1.5rem;
        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        border-radius: 12px;
        border-left: 4px solid #a858f7;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        pointer-events: auto;
        margin-top: 4rem; /* More space to avoid overlapping with title */
        display: inline-flex;
        flex-direction: column;
      }

      .message-title {
        font-weight: bold;
        font-size: 1.1em;
        margin-bottom: 0.5rem;
        color: #1f2937;
      }

      .message-buttons {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;
        flex-wrap: wrap;
      }

      .btn-download, .btn-website {
        display: inline-block;
        padding: 0.75rem 1.5rem;
        border-radius: 8px;
        text-decoration: none;
        font-weight: 600;
        font-size: 0.9rem;
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
      }

      .btn-download {
        background: linear-gradient(135deg, #a858f7 0%, #8b5cf6 100%);
        color: white;
      }

      .btn-download:hover {
        background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(168, 88, 247, 0.4);
      }

      .btn-website {
        background: transparent;
        color: #a858f7;
        border: 2px solid #a858f7;
      }

      .btn-website:hover {
        background: #a858f7;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(168, 88, 247, 0.3);
      }

      .message-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .message-header {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        position: relative;
      }

      .message-header i {
        color: #a858f7;
        font-size: 1.2rem;
        margin-top: 0.2rem;
        flex-shrink: 0;
      }

      .message-text {
        flex: 1;
      }

      .message-text p {
        margin: 0;
        font-size: 1rem;
        line-height: 1.6;
        color: #374151;
      }

      .close-message {
        background: none;
        border: none;
        color: #6b7280;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 4px;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }

      .close-message:hover {
        background: rgba(0, 0, 0, 0.1);
        color: #374151;
      }

      .close-message i {
        font-size: 1rem;
        margin: 0;
      }

      @media (max-width: 768px) {
        .export-title {
          font-size: 2rem;
        }

        .export-header {
          padding: 2rem 1rem;
        }

        .export-content {
          padding: 1rem;
        }

        .branding-content {
          flex-direction: column;
          gap: 1rem;
        }

        [data-type='draggable-item'] h2 {
          padding: 16px;
          font-size: 1.1em;
        }

        .delayed-message-overlay {
          padding: 0.5rem 1rem;
        }

        .delayed-message {
          margin-top: 2rem;
          padding: 1rem;
        }

        .message-header {
          flex-direction: column;
          gap: 0.5rem;
        }

        .message-header i {
          align-self: center;
        }

        .close-message {
          align-self: flex-end;
          margin-top: 0.5rem;
        }
        
        .message-buttons {
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .btn-download, .btn-website {
          padding: 0.75rem 1rem;
          font-size: 0.85rem;
          text-align: center;
        }
      }
    `;
  }

  /**
   * Extract TCVelik data from HTML content
   * @param {string} htmlContent - HTML content containing embedded JSON
   * @returns {Object|null} Extracted data object or null if not found
   */
  extractDataFromHTML(htmlContent) {
    try {
      // Look for window.TCVelikExportData in the HTML
      const scriptMatch = htmlContent.match(/window\.TCVelikExportData\s*=\s*(\{[\s\S]*?\});/);
      if (scriptMatch) {
        return JSON.parse(scriptMatch[1]);
      }

      // Fallback: try to find any JSON-like structure in script tags
      const scriptTags = htmlContent.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
      if (scriptTags) {
        for (const script of scriptTags) {
          const jsonMatch = script.match(/(\{[\s\S]*"Title"[\s\S]*"Content"[\s\S]*\})/);
          if (jsonMatch) {
            try {
              return JSON.parse(jsonMatch[1]);
            } catch (e) {
              continue;
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error extracting data from HTML:', error);
      return null;
    }
  }

  /**
   * Escape HTML characters
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Export as singleton instance
export default new TCVelikExportImportService();
